import styled from "styled-components";
import { Link } from "react-router-dom";

export const Page = styled.main`
  padding: 138px clamp(16px, 5vw, 72px) 80px;
  background:
    radial-gradient(circle at 85% 8%, rgba(255, 169, 0, 0.16), transparent 25%),
    ${({ theme }) => theme.color.light};
`;

export const Hero = styled.header`
  max-width: 1180px;
  margin: 0 auto 38px;
`;

export const Eyebrow = styled.p`
  display: inline-flex;
  margin: 0 0 14px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(28, 93, 39, 0.1);
  color: ${({ theme }) => theme.color.green};
  font-size: 14px;
  font-weight: 700;
`;

export const Title = styled.h1`
  max-width: 780px;
  margin: 0;
  font-size: clamp(38px, 6vw, 68px);
  line-height: 1.02;
  letter-spacing: -0.045em;
`;

export const Lead = styled.p`
  max-width: 720px;
  margin: 20px 0 0;
  color: #50544f;
  font-size: 19px;
  line-height: 1.6;
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 0.82fr) minmax(0, 1.18fr);
  gap: 24px;
  max-width: 1180px;
  margin: 0 auto;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section`
  min-width: 0;
  padding: clamp(22px, 4vw, 38px);
  border: 1px solid rgba(29, 29, 27, 0.08);
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 22px 65px rgba(24, 53, 30, 0.09);
`;

export const Form = styled.form`
  display: grid;
  gap: 17px;
`;

export const Field = styled.label`
  display: grid;
  gap: 7px;
  font-size: 14px;
  font-weight: 700;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

export const Hint = styled.span`
  color: #747874;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
`;

export const Input = styled.input`
  width: 100%;
  min-height: 50px;
  padding: 0 15px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#a62d2d" : "#d8ddd8")};
  border-radius: 14px;
  background: #fbfcfa;
  color: ${({ theme }) => theme.color.black};
  font: inherit;

  &:focus {
    outline: 3px solid rgba(255, 169, 0, 0.25);
    border-color: ${({ theme }) => theme.color.orange};
  }
`;

export const Select = styled.select`
  width: 100%;
  min-height: 50px;
  padding: 0 15px;
  border: 1px solid #d8ddd8;
  border-radius: 14px;
  background: #fbfcfa;
  color: ${({ theme }) => theme.color.black};
  font: inherit;
`;

export const Error = styled.span`
  color: #a62d2d;
  font-size: 12px;
  font-weight: 500;
`;

export const Submit = styled.button`
  min-height: 54px;
  margin-top: 4px;
  border: 0;
  border-radius: 999px;
  background: ${({ theme }) => theme.color.orange};
  color: ${({ theme }) => theme.color.black};
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(255, 169, 0, 0.28);
  }
`;

export const ResultHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 22px;

  h2 { margin: 0; font-size: clamp(26px, 4vw, 38px); }
  p { margin: 6px 0 0; color: #676b67; }
`;

export const Baseline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f3f5f1;
  color: #525752;
  font-size: 14px;

  strong { color: ${({ theme }) => theme.color.black}; }
`;

export const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

export const ResultCard = styled.article`
  padding: 20px;
  border-radius: 20px;
  background: ${({ $accent, theme }) => $accent ? theme.color.green : "#f5f6f3"};
  color: ${({ $accent }) => $accent ? "#fff" : "inherit"};

  h3 { margin: 0 0 14px; font-size: 18px; }
  dl { display: grid; gap: 12px; margin: 0; }
  div { display: grid; gap: 2px; }
  dt { opacity: 0.78; font-size: 12px; }
  dd { margin: 0; font-size: 20px; font-weight: 800; }
  p { font-size: 12px; line-height: 1.5; }
`;

export const ScheduleButton = styled.button`
  width: 100%; min-height: 44px; padding: 10px 12px; margin-top: 16px;
  border: 1px solid currentColor; border-radius: 12px; background: transparent;
  color: inherit; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
  &:focus-visible { outline: 3px solid #ffa900; outline-offset: 3px; }
`;

export const ChartBox = styled.div`
  margin-top: 18px;
  padding: 18px;
  border: 1px solid #e0e4df;
  border-radius: 20px;
  overflow: hidden;

  svg { display: block; width: 100%; height: auto; }
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 10px;
  font-size: 12px;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "";
    width: 18px;
    height: 3px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
  }
`;

export const Details = styled.details`
  scroll-margin-top: 120px;
  margin-top: 18px;
  border-top: 1px solid #e4e7e2;
  padding-top: 18px;

  summary { cursor: pointer; font-weight: 800; }
`;

export const TableControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 16px 0 10px;
  font-size: 13px;

  select { min-height: 40px; width: auto; }
`;

export const TableWrap = styled.div`
  max-width: 100%;
  max-height: 430px;
  overflow: auto;
  border: 1px solid #e0e4df;
  border-radius: 14px;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
  font-size: 13px;

  th, td { padding: 10px 12px; text-align: right; border-bottom: 1px solid #eceeeb; }
  th:first-child, td:first-child { text-align: left; }
  th { position: sticky; top: 0; background: #f3f5f1; z-index: 1; }
`;

export const Disclaimer = styled.div`
  max-width: 1180px;
  margin: 24px auto 0;
  padding: 22px 24px;
  border-radius: 18px;
  background: rgba(28, 93, 39, 0.07);
  color: #545954;

  h2 { margin: 0 0 8px; font-size: 18px; }
  p { margin: 0; font-size: 13px; line-height: 1.65; }
`;

export const CTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-top: 18px;
  padding: 0 24px;
  border-radius: 999px;
  background: ${({ theme }) => theme.color.green};
  color: #fff;
  font-weight: 800;
  text-decoration: none;
`;

export const AuditBox = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 28px;
  max-width: 1180px;
  margin: 24px auto 0;
  padding: clamp(24px, 4vw, 38px);
  border-radius: 26px;
  background: ${({ theme }) => theme.color.green};
  color: #fff;

  h2 { max-width: 680px; margin: 0 0 10px; font-size: clamp(25px, 4vw, 38px); }
  p { max-width: 760px; margin: 0; color: rgba(255,255,255,.82); line-height: 1.65; }
  .guide-note { display: grid; gap: 2px; max-width: 620px; margin-top: 18px; padding: 13px 15px; border: 1px solid rgba(255,255,255,.18); border-radius: 14px; background: rgba(255,255,255,.07); }
  .guide-note span { color: rgba(255,255,255,.72); font-size: 13px; }
  ${Eyebrow} { background: rgba(255,255,255,.12); color: #fff; }
  ${CTA} { flex: 0 0 auto; margin: 0; background: ${({ theme }) => theme.color.orange}; color: ${({ theme }) => theme.color.black}; }

  @media (max-width: 760px) { align-items: flex-start; flex-direction: column; }
`;

